import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const oldValue = this.refs.cartBubbleCount.textContent;
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    
    // Also update any other bubble count elements on the page (cart drawer, etc)
    const allBubbles = document.querySelectorAll('[ref="cartBubbleCount"]');
    allBubbles.forEach(bubble => {
      bubble.textContent = value < 100 ? String(value) : '';
    });
    
    if (oldValue !== String(value)) {
      const stack = new Error().stack;
    }
  }

  /**
   * Calculate adjusted cart count accounting for bundled items.
   * Bundles (4 items) should count as 1 item.
   * @returns {number} The adjusted item count
   */
  getAdjustedCartCount() {
    const totalItems = document.querySelectorAll('tr[data-key]').length;
    const bundleChildItems = document.querySelectorAll('.cart-items__bundle-child').length;
    return totalItems - bundleChildItems;
  }

  connectedCallback() {
    super.connectedCallback();

    // Check if we're on a cart page and update the count immediately
    const totalItems = document.querySelectorAll('tr[data-key]').length;
    if (totalItems > 0) {
      this.renderCartBubble(totalItems, false, false);
    }
    
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('cart:count-update', this.onCartCountUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('cart:count-update', this.onCartCountUpdate);
  }

  /**
   * Handles custom cart count update event (for bundle grouping).
   * @param {CustomEvent} event - The cart count update event.
   */
  onCartCountUpdate = (event) => {
    const adjustedCount = event.detail?.count ?? 0;
    this.currentCartCount = adjustedCount;
    this.refs.cartBubbleCount.classList.toggle('hidden', adjustedCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', adjustedCount === 0);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    // Always use renderCartBubble - it will detect bundles via data-bundle-group attribute
    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Check if we have bundle items - if so, use adjusted count
    const totalItems = document.querySelectorAll('tr[data-key]').length;
    
    // Look for bundles by data-bundle-group attribute (more reliable than waiting for class)
    const bundleRows = document.querySelectorAll('tr[data-bundle-group]');
    let displayCount = itemCount;
    
    if (bundleRows.length > 0) {
      // Count unique bundle groups
      const bundleGroups = new Set();
      bundleRows.forEach(row => {
        const bundleGroup = row.getAttribute('data-bundle-group');
        if (bundleGroup) {
          bundleGroups.add(bundleGroup);
        }
      });
      // Each bundle group counts as 1, plus non-bundled items
      const nonBundledItems = totalItems - bundleRows.length;
      displayCount = bundleGroups.size + nonBundledItems;

    } 

    this.refs.cartBubbleCount.classList.toggle('hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', displayCount > 0 && animate);

    const finalCount = comingFromProductForm ? this.currentCartCount + itemCount : displayCount;
  
    this.currentCartCount = finalCount;
  

    this.classList.toggle('header-actions__cart-icon--has-cart', displayCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
